"use client";

import styles from './Header.module.scss';

import useScroll from "@/hooks/useScroll";
import { Outlet } from "@/types/language";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useMemo, useState } from "react";
import { Buttton } from '../common';
import ThemeToggle from '../ui/ThemeToggle';
import { LivePlayer } from '../ui/LivePlayer';
import MobileMenu from './MobileMenu';
import { motion, AnimatePresence, Variants } from 'motion/react';
import MediaPlayerButton from '../media/MediaPlayerButton';
import { translate } from '@/utils/translateUtils';

type HeaderProps = {
  outlet: Outlet;
  dictionary: any;
  navigation: any;
};

type LanguageItem = {
  name: string;
  locale: string;
};

const LOGO_MINI = '/images/logo-mini.svg';

const OUTLET_LANGUAGES: Record<Outlet, LanguageItem[]> = {
  iitv: [
    { name: "العربية", locale: "ar" },
    { name: "فارسی", locale: "fa" },
    { name: "English", locale: "en" },
  ],
  aitv: [
    { name: "پښتو", locale: "pa" },
    { name: "فارسی", locale: "fa" },
    { name: "English", locale: "en" },
  ],
};

const shortOutletNameMap: Record<Outlet, string> = {
  iitv: "ii",
  aitv: "ai",
};

const getSupportedLanguages = (outlet: Outlet): LanguageItem[] => OUTLET_LANGUAGES[outlet] || [];

const generateLogoUrl = (outlet: Outlet, lang: string): string => {
  const shortOutletName = shortOutletNameMap[outlet];
  return `/images/${shortOutletName}/${shortOutletName}-logo-${lang || 'fa'}.svg`;
};

const scrollToTop = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const logoAnimationVariants: Variants = {
  initial: { 
    opacity: 0,
    y: 20,
    scale: 0.8,
  },
  enter: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] // Custom cubic-bezier curve for smooth animation
    }
  },
  exit: { 
    opacity: 0,
    y: -20,
    scale: 0.9,
    transition: { 
      duration: 0.3,
      ease: [0.65, 0, 0.35, 1]
    }
  }
};

const headerVariants = {
  initial: { y: 0 },
  scrolled: {
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    backgroundColor: "var(--background-color)",
    height: "60px",
    transition: { duration: 0.3 }
  }
};

export default function Header({ outlet, dictionary, navigation }: HeaderProps) {

  const { lang = "fa" } = useParams<{ lang: string }>();
  const { isScrolled } = useScroll({ threshold: 50.5, debounceDelay: 300 });

  const availableLanguages = useMemo(
    () => getSupportedLanguages(outlet).filter((item: LanguageItem) => item.locale !== lang),
    [outlet, lang]
  );

  const logo = useMemo(
    () => <Image
      alt="Brand"
      src={generateLogoUrl(outlet, lang)}
      width={80}
      height={40}
      priority
      className={styles.logo}
    />,
    [outlet, lang]
  );
  const miniLogo = <Image alt="Brand Mini" src={LOGO_MINI} width={32} height={32} priority className={styles.logo} />;

  const [menuVisible, setMenuVisible] = useState<boolean>(false);
  const [playerVisible, setPlayerVisible] = useState<boolean>(false);

  const toggleMenuVisibility = () => setMenuVisible((prev) => !prev);

  const togglePlayerVisibility = () => {
    if (!playerVisible) scrollToTop();
    setPlayerVisible((prev) => !prev);
  };


  return (
    <>
      {/* Top Language Switcher */}
      <div className={styles.top}>
        <div className={styles.top__container}>
          <nav className={styles.languageSwitcher}>
            <ul className={styles.languageList}>
              {availableLanguages.map(({ name, locale }) => (
                <li key={locale} className={styles.languageItem}>
                  <Link href={`/${locale}`} className={styles.languageLink} prefetch={false} shallow={lang === 'fa'}>
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Header Navigation */}
      <motion.header
        className={`${styles.header} ${isScrolled ? styles.headerScrolled : ''}`}
      >
        <nav className={styles.nav}>
          {/* Logo */}
          <Link 
            href={`/${lang === 'fa' ? '' : lang}`}
            className={styles.logoLink}
            prefetch={false}
          >
            <AnimatePresence mode="wait">
              {isScrolled ? <motion.div
                key="mini-logo"
                variants={logoAnimationVariants}
                initial="initial"
                animate="enter"
                exit="exit"
              >{miniLogo}</motion.div> : <motion.div
                key="main-logo"
                variants={logoAnimationVariants}
                initial="initial"
                animate="enter"
                exit="exit"
              >{logo}</motion.div>}
            </AnimatePresence>
          </Link>
          {/* Navigation Menu */}
          <ul className={styles.navList}>
            {navigation.menu.map(({ title, link }: { title: string, link: string }) => (
              <li key={link} className={styles.navItem}>
                <Link href={`${lang === 'fa' ? '' : `/${lang}`}/${link}`} className={styles.navLink} prefetch={false}>
                  {title}
                </Link>
              </li>
            ))}
          </ul>

          {/* Header Options */}
          <div className={styles.navOptions}>
            <Link className={styles.searchButton} href={`${lang === 'fa' ? '' : `/${lang}`}/search`} prefetch={false}>
              <svg height="20" strokeLinejoin="round" viewBox="0 0 16 16" width="24" style={{ color: "currentcolor" }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M1.5 6.5C1.5 3.73858 3.73858 1.5 6.5 1.5C9.26142 1.5 11.5 3.73858 11.5 6.5C11.5 9.26142 9.26142 11.5 6.5 11.5C3.73858 11.5 1.5 9.26142 1.5 6.5ZM6.5 0C2.91015 0 0 2.91015 0 6.5C0 10.0899 2.91015 13 6.5 13C8.02469 13 9.42677 12.475 10.5353 11.596L13.9697 15.0303L14.5 15.5607L15.5607 14.5L15.0303 13.9697L11.596 10.5353C12.475 9.42677 13 8.02469 13 6.5C13 2.91015 10.0899 0 6.5 0Z" fill="currentColor"></path>
              </svg>
            </Link>
            <MediaPlayerButton
              type="stream"
              media={outlet === 'iitv' ? 
                'https://live.livetvstream.co.uk/LS-63503-4/index.m3u8' : 'https://afintlbak.livetvstream.co.uk/aitv/index.m3u8'}
              metadata={{
                title: translate('widget.live.title', dictionary),
                poster: outlet === 'iitv' ?
                  'https://i.iranintl.com/images/rdk9umy0/production/8faf1c9dd44bbca0ece3a5b9b14587db3efbeceb-1920x1080.jpg?w=640&h=360&auto=format'
                  : 'https://i.afintl.com/images/rdk9umy0/production/b04681f8d83c07ec912433f186bbccd182c4909a-2480x1396.png?w=640&h=360&auto=format',
              }}
            />
            <ThemeToggle className={styles.themeToggleButton} />
            <Buttton className={styles.toggleMenu} onClick={toggleMenuVisibility} data-state={menuVisible ? 'expanded' : 'closed'}>
              <svg aria-hidden="true" viewBox="0 0 16 16" width='24px' height='24px'>
                <rect x="1" y="3" width="14" height="1.5"></rect>
                <rect x="1" y="7" width="14" height="1.5"></rect>
                <rect x="1" y="11" width="14" height="1.5"></rect>
              </svg>
            </Buttton>
          </div>
        </nav>
      </motion.header>

      {/* Live Player and Mobile Menu */}
      <LivePlayer
        show={playerVisible}
        src={`${process.env.OTT_PROVIDER}/tv`}
      />

      <MobileMenu
        isVisiable={menuVisible}
        navigation={navigation}
        dictionary={dictionary}
        otherLanguages={availableLanguages}
        onClick={toggleMenuVisibility}
      />
    </>
  );
}

/*
            <Buttton className={styles.livePlayerButton} onClick={togglePlayerVisibility}>
              <svg
                height="24"
                strokeLinejoin="round"
                viewBox="0 0 16 16"
                width="24"
                style={{ color: "currentcolor" }}>
                <path fillRule="evenodd" clipRule="evenodd" d="M1.5 2.5H14.5V12.5C14.5 13.0523 14.0523 13.5 13.5 13.5H2.5C1.94772 13.5 1.5 13.0523 1.5 12.5V2.5ZM0 1H1.5H14.5H16V2.5V12.5C16 13.8807 14.8807 15 13.5 15H2.5C1.11929 15 0 13.8807 0 12.5V2.5V1ZM6.745 6.76445L9.2161 8L6.745 9.23555V6.76445ZM11.1056 7.55279C11.4741 7.73705 11.4741 8.26295 11.1056 8.44721L6.22361 10.8882C5.89116 11.0544 5.5 10.8127 5.5 10.441L5.5 5.55902C5.5 5.18733 5.89116 4.94558 6.22361 5.1118L11.1056 7.55279Z" fill="currentColor"></path>
              </svg>
            </Buttton>
*/